import React, { useState } from 'react'
import {Mail, Phone, MapPin, Clock, Send, Instagram, Facebook, Linkedin} from 'lucide-react'

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    
    try {
      // Simulation d'envoi - remplacer par appel Deno fonction pour envoi email réel
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      console.log('Form submitted:', formData)
      setSubmitStatus('success')
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      })
      
      // Masquer le message après 5 secondes
      setTimeout(() => setSubmitStatus('idle'), 5000)
    } catch (error) {
      console.error('Error submitting form:', error)
      setSubmitStatus('error')
      setTimeout(() => setSubmitStatus('idle'), 5000)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <section id="contact" className="bg-zinc-950 py-16 sm:py-20 md:py-24 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/10 via-white/5 to-red-500/10 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 md:mb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <span className="text-blue-400 font-bold text-xs sm:text-sm uppercase tracking-widest mb-3 sm:mb-4 block">Contact</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 sm:mb-6 px-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            Parlons de votre <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-white to-red-500">projet</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-white/60 max-w-2xl mx-auto px-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Notre équipe est à votre disposition
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          <div className="space-y-5 sm:space-y-6 md:space-y-8 animate-in fade-in slide-in-from-left duration-700">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 hover:bg-white/10 hover:border-blue-400/30 transition-all hover:scale-105 group">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 group-hover:scale-110 transition-transform flex-shrink-0">
                  <MapPin className="text-white" size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Adresse</h3>
                  <p className="text-white/70 leading-relaxed text-xs sm:text-sm md:text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    554 Route de Calas<br />
                    Les Pennes Mirabeau 13170<br />
                    Plan de Campagne<br />
                    10km d'Aix-en-Provence · 20km de Marseille
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 hover:bg-white/10 hover:border-white/20 transition-all hover:scale-105 group">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-white/20 to-white/10 rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 group-hover:scale-110 transition-transform flex-shrink-0">
                  <Phone className="text-white" size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Téléphone</h3>
                  <a href="tel:+33412333650" className="text-white/70 hover:text-blue-400 transition-colors text-sm sm:text-base md:text-lg font-semibold block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    04 12 33 36 50
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 hover:bg-white/10 hover:border-red-400/30 transition-all hover:scale-105 group">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 group-hover:scale-110 transition-transform flex-shrink-0">
                  <Mail className="text-white" size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Email</h3>
                  <a href="mailto:contact@autostar13.com" className="text-white/70 hover:text-red-400 transition-colors text-xs sm:text-sm md:text-base break-all block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    contact@autostar13.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 hover:bg-white/10 hover:border-blue-300/30 transition-all hover:scale-105 group">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-blue-400 to-blue-500 rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 group-hover:scale-110 transition-transform flex-shrink-0">
                  <Clock className="text-white" size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Horaires</h3>
                  <p className="text-white/70 leading-relaxed text-xs sm:text-sm md:text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Lundi - Samedi : 9h - 19h<br />
                    Dimanche : Sur rendez-vous
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 justify-center lg:justify-start">
              <a href="#" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-full p-3 sm:p-3.5 md:p-4 hover:bg-blue-600 hover:border-blue-400 transition-all hover:scale-110">
                <Facebook className="text-white" size={20} />
              </a>
              <a href="#" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-full p-3 sm:p-3.5 md:p-4 hover:bg-gradient-to-br from-red-500 to-red-600 hover:border-red-400 transition-all hover:scale-110">
                <Instagram className="text-white" size={20} />
              </a>
              <a href="#" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-full p-3 sm:p-3.5 md:p-4 hover:bg-blue-500 hover:border-blue-400 transition-all hover:scale-110">
                <Linkedin className="text-white" size={20} />
              </a>
            </div>
          </div>

          <div className="animate-in fade-in slide-in-from-right duration-700" style={{ animationDelay: '0.2s' }}>
            <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl md:rounded-3xl p-5 sm:p-8 md:p-10 space-y-4 sm:space-y-5 md:space-y-6 hover:bg-white/10 transition-all">
              <div>
                <label className="block text-white font-semibold mb-2 sm:mb-3 text-xs sm:text-sm uppercase tracking-wide" style={{ fontFamily: 'Syne, sans-serif' }}>Nom complet</label>
                <input 
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/20 rounded-lg sm:rounded-xl px-4 sm:px-5 md:px-6 py-3 sm:py-3.5 md:py-4 text-white text-sm sm:text-base focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all"
                  placeholder="Jean Dupont"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2 sm:mb-3 text-xs sm:text-sm uppercase tracking-wide" style={{ fontFamily: 'Syne, sans-serif' }}>Email</label>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/20 rounded-lg sm:rounded-xl px-4 sm:px-5 md:px-6 py-3 sm:py-3.5 md:py-4 text-white text-sm sm:text-base focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all"
                  placeholder="jean.dupont@example.com"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2 sm:mb-3 text-xs sm:text-sm uppercase tracking-wide" style={{ fontFamily: 'Syne, sans-serif' }}>Téléphone</label>
                <input 
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/20 rounded-lg sm:rounded-xl px-4 sm:px-5 md:px-6 py-3 sm:py-3.5 md:py-4 text-white text-sm sm:text-base focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all"
                  placeholder="06 12 34 56 78"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2 sm:mb-3 text-xs sm:text-sm uppercase tracking-wide" style={{ fontFamily: 'Syne, sans-serif' }}>Message</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full bg-black/50 border border-white/20 rounded-lg sm:rounded-xl px-4 sm:px-5 md:px-6 py-3 sm:py-3.5 md:py-4 text-white text-sm sm:text-base focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all resize-none"
                  placeholder="Décrivez votre projet..."
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                />
              </div>

              <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white py-3.5 sm:py-4 md:py-5 rounded-full font-bold text-sm sm:text-base md:text-lg hover:from-blue-500 hover:to-blue-400 transition-all flex items-center justify-center gap-3 shadow-2xl hover:shadow-blue-500/50 hover:scale-105 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                <Send className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform flex-shrink-0" size={20} />
              </button>
              
              {submitStatus === 'success' && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 text-center animate-in fade-in slide-in-from-bottom-4">
                  <p className="text-green-400 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    ✓ Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                  </p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-center animate-in fade-in slide-in-from-bottom-4">
                  <p className="text-red-400 font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    ✗ Une erreur est survenue. Veuillez réessayer ou nous contacter directement.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}
